// Import the core angular services.
import {
  Directive,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { LazyTarget, LazyViewport } from '../services/lazy-viewport.service';

// Import the application components and services.

// ----------------------------------------------------------------------------------- // -----------------------------------------------------------------------------------
@Directive({
  selector: '[lazySrc]',
  inputs: ['src: lazySrc', 'visibleClass: lazySrcVisible'],
  standalone: true,
})
export class LazySrcDirective implements OnInit, OnDestroy, LazyTarget {
  private lazyViewport = inject(LazyViewport);

  public element: Element;
  public src: string;
  public visibleClass: string;
  private renderer = inject(Renderer2);

  /** Inserted by Angular inject() migration for backwards compatibility */

  // I initialize the lazy-src directive.
  constructor() {
    const elementRef = inject(ElementRef);
    const lazyViewport = this.lazyViewport;

    this.element = elementRef.nativeElement;
    this.lazyViewport = lazyViewport;
    this.src = '';
    this.visibleClass = '';
  }

  // ---
  // PUBLIC METHODS.
  // ---

  // I get called once when the directive is being destroyed.
  public ngOnDestroy(): void {
    // If we haven't detached from the LazyViewport, do so now.
    this.lazyViewport && this.lazyViewport.removeTarget(this);
  }

  // I get called once after the inputs have been bound for the first time.
  public ngOnInit(): void {
    // Attached this directive the LazyViewport so that we can be alerted to changes
    // in this element's visibility on the page.
    this.lazyViewport.addTarget(this);
  }

  // I get called by the LazyViewport service when the element associated with this
  // directive has its visibility changed.
  public updateVisibility(isVisible: boolean, ratio: number): void {
    // When this target starts being tracked by the viewport, the initial visibility
    // will be reported, even if it is not visible. As such, let's ignore the first
    // visibility update.
    if (!isVisible) {
      return;
    }

    // Now that the element is visible, load the underlying SRC value. And, since we
    // no longer need to worry about loading, we can detach from the LazyViewport.
    this.lazyViewport.removeTarget(this);
    this.renderer.setProperty(this.element, 'src', this.src);

    // If an active class has been provided, add it to the element.
    this.visibleClass &&
      this.renderer.addClass(this.element, this.visibleClass);
  }
}
